"use client";

import { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import LemonMark from "@/components/ui/LemonMark";
import {
  Search,
  History,
  Workflow,
  CalendarClock,
  Activity,
  Users,
  FileText,
  Library,
  Plug,
  Plus,
  ChevronDown,
  Target,
  Megaphone,
  MessageSquare,
  PenLine,
  Compass,
  BarChart3,
  MessageCircle,
  MessageCircleWarning,
  Inbox,
  Sun,
  PanelLeftClose,
  LayoutGrid,
  Settings,
  LogOut,
} from "lucide-react";

type IconProps = { size: number; style?: React.CSSProperties };

type AssistantCard = {
  Icon: (props: IconProps) => React.ReactElement;
  title: string;
  description: string;
};

const readyAssistants: AssistantCard[] = [
  {
    Icon: (p) => <Target {...p} />,
    title: "Lead finder",
    description: "Finds and qualifies leads for your business.",
  },
  {
    Icon: (p) => <Search {...p} />,
    title: "Deep research",
    description:
      "Researches any topic across the web and writes a cited report.",
  },
  {
    Icon: (p) => <Megaphone {...p} />,
    title: "Social media content",
    description: "Plans and writes 7 days of on-brand posts in your voice.",
  },
  {
    Icon: (p) => <MessageSquare {...p} />,
    title: "Chat",
    description:
      "Your general-purpose assistant — ask anything, switch models any time.",
  },
];

const addableAssistants: AssistantCard[] = [
  {
    Icon: (p) => <PenLine {...p} />,
    title: "Content",
    description:
      "Turn rough notes, a topic, or a link into something publishable — posts, pages, and more.",
  },
  {
    Icon: (p) => <Compass {...p} />,
    title: "Strategic Advisor",
    description:
      "A thought partner for decisions, strategy, and hard calls — asks the right questions.",
  },
  {
    Icon: (p) => <BarChart3 {...p} />,
    title: "Data Analyst",
    description:
      "Drop in a spreadsheet, paste some numbers, or point it at a dataset — it finds the story.",
  },
];

const workspaceNav = [
  {
    label: "Assistants",
    Icon: (p: IconProps) => <LayoutGrid {...p} />,
    active: true,
    children: [
      { label: "Chat History", Icon: (p: IconProps) => <History {...p} /> },
    ],
  },
  {
    label: "Workflows",
    Icon: (p: IconProps) => <Workflow {...p} />,
    children: [
      { label: "Scheduled", Icon: (p: IconProps) => <CalendarClock {...p} /> },
      { label: "Run History", Icon: (p: IconProps) => <Activity {...p} /> },
    ],
  },
  { label: "Team", Icon: (p: IconProps) => <Users {...p} />, children: [] },
];

const setupNav = [
  { label: "Skills", Icon: (p: IconProps) => <FileText {...p} /> },
  { label: "Knowledge", Icon: (p: IconProps) => <Library {...p} /> },
  { label: "Integrations", Icon: (p: IconProps) => <Plug {...p} /> },
];

export default function DashboardAppPreview({
  className = "",
  glass = false,
}: {
  className?: string;
  /** Renders the outer window frame as frosted glass, matching
      ChatAppPreview's `glass` recipe — needs a glow blob behind it. */
  glass?: boolean;
}) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  const isDark = mounted && resolvedTheme === "dark";

  const c = isDark
    ? {
        shadow: "0 30px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.04)",
        chromeBg: "#0d0d0e",
        chromeBorder: "rgba(255,255,255,0.08)",
        chromeDot: "rgba(255,255,255,0.16)",
        glassBg: "rgba(13,13,14,0.55)",
        glassBorder: "rgba(255,255,255,0.1)",
        surfaceBg: "#0d0d0e",
        sidebarBg: "transparent",
        sidebarBorder: "rgba(255,255,255,0.08)",
        logoText: "rgba(255,255,255,0.9)",
        searchBg: "rgba(255,255,255,0.06)",
        searchBorder: "rgba(255,255,255,0.08)",
        searchText: "rgba(255,255,255,0.35)",
        kbdBg: "rgba(255,255,255,0.08)",
        kbdText: "rgba(255,255,255,0.4)",
        sectionLabel: "rgba(255,255,255,0.3)",
        navText: "rgba(255,255,255,0.55)",
        navIcon: "rgba(255,255,255,0.4)",
        navActiveBg: "rgba(255,255,255,0.08)",
        navActiveText: "rgba(255,255,255,0.92)",
        navLineColor: "rgba(255,255,255,0.12)",
        planCardBg: "#000000",
        planCardBorder: "rgba(255,255,255,0.14)",
        planText: "rgba(255,255,255,0.85)",
        planSubText: "rgba(255,255,255,0.4)",
        usageLabel: "rgba(255,255,255,0.3)",
        usageTrack: "rgba(255,255,255,0.1)",
        usageFill: "#e0a730",
        avatarBg: "rgba(255,255,255,0.1)",
        avatarText: "rgba(255,255,255,0.75)",
        badgeBg: "#c2452f",
        badgeText: "#ffffff",
        headingText: "rgba(255,255,255,0.95)",
        subText: "rgba(255,255,255,0.45)",
        tabGroupBg: "rgba(255,255,255,0.04)",
        tabGroupBorder: "rgba(255,255,255,0.06)",
        tabActiveBg: "rgba(255,255,255,0.12)",
        tabActiveText: "#ffffff",
        tabActiveShadow:
          "0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.06)",
        tabText: "rgba(255,255,255,0.45)",
        cardBg: "#1b1b1b",
        cardBorder: "rgba(255,255,255,0.07)",
        cardIconBg: "rgba(255,255,255,0.06)",
        cardIcon: "rgba(255,255,255,0.7)",
        cardTitle: "rgba(255,255,255,0.92)",
        cardDesc: "rgba(255,255,255,0.42)",
        tagBg: "rgba(255,255,255,0.06)",
        tagText: "rgba(255,255,255,0.45)",
        sectionHeadText: "rgba(255,255,255,0.55)",
        footerIcon: "rgba(255,255,255,0.4)",
        footerText: "rgba(255,255,255,0.5)",
      }
    : {
        shadow: "0 30px 80px rgba(0,0,0,0.16), 0 0 0 1px rgba(0,0,0,0.02)",
        chromeBg: "#f1efe9",
        chromeBorder: "rgba(0,0,0,0.06)",
        chromeDot: "rgba(0,0,0,0.14)",
        glassBg: "rgba(255,255,255,0.55)",
        glassBorder: "rgba(255,255,255,0.7)",
        surfaceBg: "#E3E3DF",
        sidebarBg: "#F4F4F2",
        sidebarBorder: "rgba(0,0,0,0.06)",
        logoText: "rgba(0,0,0,0.85)",
        searchBg: "rgba(255,255,255,0.7)",
        searchBorder: "rgba(0,0,0,0.08)",
        searchText: "rgba(0,0,0,0.35)",
        kbdBg: "rgba(0,0,0,0.05)",
        kbdText: "rgba(0,0,0,0.4)",
        sectionLabel: "rgba(0,0,0,0.32)",
        navText: "rgba(0,0,0,0.6)",
        navIcon: "rgba(0,0,0,0.45)",
        navActiveBg: "rgba(0,0,0,0.07)",
        navActiveText: "rgba(0,0,0,0.88)",
        navLineColor: "rgba(0,0,0,0.1)",
        planCardBg: "rgba(255,255,255,0.6)",
        planCardBorder: "rgba(0,0,0,0.08)",
        planText: "rgba(0,0,0,0.82)",
        planSubText: "rgba(0,0,0,0.4)",
        usageLabel: "rgba(0,0,0,0.32)",
        usageTrack: "rgba(0,0,0,0.08)",
        usageFill: "#c9820a",
        avatarBg: "rgba(0,0,0,0.08)",
        avatarText: "rgba(0,0,0,0.7)",
        badgeBg: "#c2452f",
        badgeText: "#ffffff",
        headingText: "rgba(0,0,0,0.88)",
        subText: "rgba(0,0,0,0.45)",
        tabGroupBg: "rgba(0,0,0,0.04)",
        tabGroupBorder: "rgba(0,0,0,0.06)",
        tabActiveBg: "#ffffff",
        tabActiveText: "rgba(0,0,0,0.88)",
        tabActiveShadow:
          "0 1px 3px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.04)",
        tabText: "rgba(0,0,0,0.45)",
        cardBg: "#ffffff",
        cardBorder: "rgba(0,0,0,0.05)",
        cardIconBg: "rgba(0,0,0,0.04)",
        cardIcon: "rgba(0,0,0,0.6)",
        cardTitle: "rgba(0,0,0,0.85)",
        cardDesc: "rgba(0,0,0,0.42)",
        tagBg: "rgba(0,0,0,0.05)",
        tagText: "rgba(0,0,0,0.45)",
        sectionHeadText: "rgba(0,0,0,0.5)",
        footerIcon: "rgba(0,0,0,0.4)",
        footerText: "rgba(0,0,0,0.55)",
      };

  const glassActive = glass;

  const renderCard = (card: AssistantCard, addable: boolean) => (
    <div
      key={card.title}
      className="flex flex-col rounded-2xl border overflow-hidden"
      style={{ background: c.cardBg, borderColor: c.cardBorder }}
    >
      <div
        className="flex h-16 items-center px-4"
        style={{ background: c.cardIconBg }}
      >
        <card.Icon size={20} />
      </div>
      <div
        className="flex flex-1 flex-col gap-2 px-4 py-4"
        style={{ color: c.cardIcon }}
      >
        <p className="text-[13px] font-medium" style={{ color: c.cardTitle }}>
          {card.title}
        </p>
        <p className="text-[11px] leading-snug" style={{ color: c.cardDesc }}>
          {card.description}
        </p>
        {addable && (
          <div className="mt-2 flex items-center justify-between gap-2">
            <span
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px]"
              style={{ background: c.tagBg, color: c.tagText }}
            >
              <Search size={10} />
              Web search
            </span>
            <span
              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium"
              style={{ background: "#FFE500", color: "#161616" }}
            >
              <Plus size={11} />
              Add
            </span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div
      className={`relative select-none text-left ${className}`}
      aria-hidden="true"
    >
      <div
        className={`flex aspect-[3/4] md:aspect-[2980/1894] overflow-hidden ${glassActive ? "hero-glass-surface" : "rounded-2xl"}`}
        style={
          glassActive
            ? undefined
            : {
                background: c.glassBg,
                borderTop: `1px solid ${c.glassBorder}`,
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }
        }
      >
        {/* Sidebar */}
        <div
          className="hidden md:flex w-[210px] shrink-0 flex-col px-4 pt-4 pb-4"
          style={{
            background: c.sidebarBg,
            borderRight: `1px solid ${c.sidebarBorder}`,
          }}
        >
          <div className="flex items-center gap-2 px-1">
            <LemonMark size={18} />
            <span
              className="text-[14px] font-medium"
              style={{ color: c.logoText }}
            >
              LaunchLemonade
            </span>
          </div>

          <div
            className="mt-3 flex items-center justify-between gap-2 rounded-lg px-3 py-2"
            style={{
              background: c.searchBg,
              border: `1px solid ${c.searchBorder}`,
            }}
          >
            <span className="flex items-center gap-2">
              <Search size={13} style={{ color: c.searchText }} />
              <span className="text-[12px]" style={{ color: c.searchText }}>
                Search...
              </span>
            </span>
            <span
              className="rounded px-1.5 py-0.5 text-[10px] font-medium"
              style={{ background: c.kbdBg, color: c.kbdText }}
            >
              ⌘K
            </span>
          </div>

          <p
            className="mt-3 px-1 text-[10px] font-medium tracking-wide"
            style={{ color: c.sectionLabel }}
          >
            WORKSPACE
          </p>
          <div className="mt-1 flex flex-col gap-0.5">
            {workspaceNav.map((item) => (
              <div key={item.label}>
                <div
                  className="flex items-center gap-2 rounded-lg px-2 py-1"
                  style={{
                    background: item.active ? c.navActiveBg : undefined,
                  }}
                >
                  <item.Icon
                    size={13}
                    style={{ color: item.active ? c.navActiveText : c.navIcon }}
                  />
                  <span
                    className="text-[12px]"
                    style={{ color: item.active ? c.navActiveText : c.navText }}
                  >
                    {item.label}
                  </span>
                </div>
                {item.children.length > 0 && (
                  <div className="relative mt-0.5 flex flex-col gap-0.5">
                    <span
                      aria-hidden="true"
                      className="absolute w-px"
                      style={{
                        left: 14.5,
                        top: 0,
                        bottom: 12,
                        background: c.navLineColor,
                      }}
                    />
                    {item.children.map((child) => (
                      <div
                        key={child.label}
                        className="flex items-center gap-2 rounded-lg px-2 py-1"
                        style={{ marginLeft: 14 }}
                      >
                        <child.Icon size={13} style={{ color: c.navIcon }} />
                        <span
                          className="text-[12px]"
                          style={{ color: c.navText }}
                        >
                          {child.label}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <p
            className="mt-3 px-1 text-[10px] font-medium tracking-wide"
            style={{ color: c.sectionLabel }}
          >
            SET UP
          </p>
          <div className="mt-1 flex flex-col gap-0.5">
            {setupNav.map(({ label, Icon }) => (
              <div
                key={label}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5"
              >
                <Icon size={13} style={{ color: c.navIcon }} />
                <span className="text-[12px]" style={{ color: c.navText }}>
                  {label}
                </span>
              </div>
            ))}
          </div>

          <div className="flex-1" />

          <div
            className="-mx-4 -mb-4 rounded-xl px-3 py-3 flex flex-col gap-3 border"
            style={{ background: c.planCardBg, borderColor: c.planCardBorder }}
          >
            <div className="flex items-center gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-medium"
                style={{ background: c.avatarBg, color: c.avatarText }}
              >
                J
              </span>
              <span className="flex flex-1 min-w-0 flex-col leading-tight">
                <span
                  className="truncate text-[11px] font-medium"
                  style={{ color: c.planText }}
                >
                  John Doe
                </span>
                <span className="text-[9px]" style={{ color: c.planSubText }}>
                  Pro plan
                </span>
              </span>
              <Settings size={13} style={{ color: c.footerIcon }} />
              <LogOut size={13} style={{ color: c.footerIcon }} />
            </div>

            <div>
              <p
                className="text-[9px] font-medium tracking-wide"
                style={{ color: c.usageLabel }}
              >
                FREE USAGE
              </p>
              <div
                className="mt-2 h-1.5 w-full rounded-full"
                style={{ background: c.usageTrack }}
              >
                <div
                  className="h-1.5 w-[8%] rounded-full"
                  style={{ background: c.usageFill }}
                />
              </div>
              <div
                className="mt-2.5 rounded-md py-1.5 text-center text-[11px] font-medium"
                style={{ background: "#FFE500", color: "#161616" }}
              >
                Upgrade to Pro
              </div>
            </div>

            <div className="flex items-center gap-1.5">
              <MessageCircleWarning size={13} style={{ color: c.footerIcon }} />
              <span className="text-[11px]" style={{ color: c.footerText }}>
                Report a problem
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <Inbox size={13} style={{ color: c.footerIcon }} />
              <span className="text-[11px]" style={{ color: c.footerText }}>
                Zestie Inbox
              </span>
              <span
                className="flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-medium"
                style={{ background: c.badgeBg, color: c.badgeText }}
              >
                2
              </span>
            </div>

            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                <Sun size={13} style={{ color: c.footerIcon }} />
                <span className="text-[11px]" style={{ color: c.footerText }}>
                  Theme
                </span>
              </span>
              <span className="flex items-center gap-1.5">
                <PanelLeftClose size={13} style={{ color: c.footerIcon }} />
                <span className="text-[11px]" style={{ color: c.footerText }}>
                  Collapse
                </span>
              </span>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div
          className="flex-1 min-w-0 flex flex-col px-6 py-6 md:px-9 md:py-9 overflow-hidden"
          style={{ background: c.surfaceBg }}
        >
          <div className="flex items-start justify-between gap-3">
            <div>
              <p
                className="text-[18px] md:text-[22px] font-medium"
                style={{ color: c.headingText }}
              >
                Workspace
              </p>
              <p
                className="mt-1.5 text-[11px] md:text-[12px]"
                style={{ color: c.subText }}
              >
                Everything you&apos;ve created — assistants you chat with and
                workflows that run for you
              </p>
            </div>
            <span
              className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-[12px] font-medium"
              style={{
                background: "#FFE500",
                boxShadow: "0 4px 16px -4px rgba(255,229,0,0.7)",
                color: "#161616",
              }}
            >
              <Plus size={13} />
              New
              <ChevronDown size={12} />
            </span>
          </div>

          <div className="mt-5 flex items-center justify-between gap-2">
            <div
              className="inline-flex items-center gap-1 rounded-full p-1"
              style={{
                background: c.tabGroupBg,
                border: `1px solid ${c.tabGroupBorder}`,
              }}
            >
              {["All", "Assistants", "Workflows"].map((tab) => (
                <span
                  key={tab}
                  className="rounded-full px-3 py-1.5 text-[11px] font-medium"
                  style={
                    tab === "Assistants"
                      ? {
                          background: c.tabActiveBg,
                          color: c.tabActiveText,
                          boxShadow: c.tabActiveShadow,
                        }
                      : { color: c.tabText }
                  }
                >
                  {tab}
                </span>
              ))}
            </div>
            <span
              className="hidden sm:inline-flex shrink-0 items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium"
              style={{
                background: c.tabActiveBg,
                color: c.tabActiveText,
                boxShadow: c.tabActiveShadow,
              }}
            >
              <Users size={12} style={{ color: c.navIcon }} />
              Everyone
              <ChevronDown size={11} />
            </span>
          </div>

          <div className="mt-5 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {readyAssistants.map((card) => renderCard(card, false))}
          </div>

          <div className="mt-6 flex items-center gap-1.5">
            <ChevronDown size={13} style={{ color: c.sectionHeadText }} />
            <span
              className="text-[12px] font-medium"
              style={{ color: c.sectionHeadText }}
            >
              Add an assistant (3)
            </span>
          </div>

          <div className="mt-4 grid grid-cols-2 lg:grid-cols-3 gap-4">
            {addableAssistants.map((card) => renderCard(card, true))}
          </div>
        </div>
      </div>
      {/* Floating chat bubble — inset so it reads as part of the dashboard, not spilling past its frame */}
      <span
        className="absolute bottom-5 right-5 hidden md:flex h-11 w-11 items-center justify-center rounded-full"
        style={{ background: "#FFE500", boxShadow: c.shadow }}
      >
        <MessageCircle
          size={19}
          strokeWidth={2.25}
          style={{ color: "#161616" }}
        />
      </span>
    </div>
  );
}
